import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  PawPrint,
  CalendarDays,
  FileText,
  Building2,
  Bell,
  Globe,
  MapPin,
  Clock3,
  LogOut,
  ChevronRight,
  Camera,
  Settings
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotificationRefresh } from '../../contexts/NotificationContext'
import Avatar from '../../components/ui/Avatar'
import { mascotaService } from '../../services/mascotaService'
import { citaService } from '../../services/citaService'
import { fichaService } from '../../services/fichaService'
import { suscripcionClinicaService } from '../../services/suscripcionClinicaService'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function userTypeLabel(user) {
  const type = (user?.tipoUsuario || user?.tipo_usuario || '').toUpperCase()
  if (type === 'DUEÑO' || type === 'DUENO') return 'DUEÑO'
  if (type === 'VETERINARIO') return 'VETERINARIO'
  if (type === 'PET_SITTER') return 'PET SITTER'
  if (type) return type
  return 'USUARIO'
}

export default function PerfilMobileExact() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { contadorNoLeidas } = useNotificationRefresh()
  const [firstPetId, setFirstPetId] = useState(null)
  const [firstFichaId, setFirstFichaId] = useState(null)
  const [counts, setCounts] = useState({ mascotasActivas: 0, citasActivas: 0, fichas: 0 })
  const [subscriptionLabel, setSubscriptionLabel] = useState('Sin activa')
  const [toastMessage, setToastMessage] = useState('')

  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      try {
        const mascotasPromise = mascotaService.getMascotasByDueno().catch(() => mascotaService.getAll({ per_page: 100 }))
        const citasPromise = citaService.getCitasByDueno().catch(() => citaService.getAll({ per_page: 100 }))
        const fichasPromise = fichaService.getAll({ per_page: 100 }).catch(() => null)
        const suscripcionesPromise = suscripcionClinicaService.getAll().catch(() => null)

        const [mascotasRes, citasRes, fichasRes, suscripcionesRes] = await Promise.all([
          mascotasPromise,
          citasPromise,
          fichasPromise,
          suscripcionesPromise
        ])

        if (!mounted) return

        const mascotas = extractArray(mascotasRes)
        const citas = extractArray(citasRes)
        const fichas = extractArray(fichasRes)
        const suscripciones = extractArray(suscripcionesRes)

        const petId = mascotas[0]?.id_mascota || mascotas[0]?.idMascota || null
        const fichaId = fichas[0]?.id_ficha || fichas[0]?.idFicha || null
        setFirstPetId(petId)
        setFirstFichaId(fichaId)

        const mascotasActivas = mascotas.filter((item) => {
          const estado = String(item.estado || 'ACTIVA').toUpperCase()
          return estado !== 'INACTIVA' && estado !== 'FALLECIDA'
        }).length
        const citasActivas = citas.filter((item) => {
          const estado = String(item.estado || '').toUpperCase()
          return !estado.includes('COMPLET') && !estado.includes('CANCEL') && !estado.includes('RECHAZ')
        }).length
        setCounts({ mascotasActivas, citasActivas, fichas: fichas.length })

        const activeSubscription =
          suscripciones.find((item) => item.estado === 'ACTIVA') ||
          suscripciones.find((item) => item.estado === 'PENDIENTE_APROBACION') ||
          null
        setSubscriptionLabel(activeSubscription?.clinica?.nombre ? `${activeSubscription.clinica.nombre} ✓` : 'Sin suscripción vigente')
      } catch {
        if (!mounted) return
        setFirstPetId(null)
        setFirstFichaId(null)
        setCounts({ mascotasActivas: 0, citasActivas: 0, fichas: 0 })
        setSubscriptionLabel('Sin suscripción vigente')
      }
    }
    fetchData()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!toastMessage) return
    const timer = setTimeout(() => setToastMessage(''), 1800)
    return () => clearTimeout(timer)
  }, [toastMessage])

  const showToast = (message) => setToastMessage(message)

  const handleLogout = () => {
    logout()
  }

  const menuItemsMain = [
    { icon: PawPrint, label: 'Mis mascotas', value: `${counts.mascotasActivas} vivas`, onClick: () => navigate('/') },
    { icon: CalendarDays, label: 'Mis citas', value: `${counts.citasActivas} abiertas`, onClick: () => navigate('/citas') },
    { icon: FileText, label: 'Fichas clínicas', value: String(counts.fichas), onClick: () => navigate(firstFichaId ? `/fichas/${firstFichaId}` : (firstPetId ? `/mascotas/${firstPetId}` : '/fichas')) }
  ]

  const menuItemsSecondary = [
    { icon: Building2, label: 'Suscripciones', value: subscriptionLabel, onClick: () => navigate('/suscripciones-clinicas') },
    { icon: Bell, label: 'Notificaciones', value: contadorNoLeidas > 0 ? String(contadorNoLeidas) : '', onClick: () => navigate('/notificaciones') },
    { icon: Globe, label: 'Idioma', value: 'Español', onClick: () => showToast('🌐 Idioma — próximamente') },
    { icon: MapPin, label: 'Ubicación', value: user?.ciudad || 'Montevideo', onClick: () => showToast(`📍 Ubicación — ${user?.ciudad || 'Montevideo, UY'}`) }
  ]

  const menuItemsDanger = [
    { icon: Clock3, label: 'Limpiar historial', value: '', onClick: () => showToast('🕓 Historial borrado') },
    { icon: LogOut, label: 'Cerrar sesión', value: '', onClick: handleLogout, danger: true }
  ]

  return (
    <div className="min-h-full bg-white pb-20">
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#555]"
          type="button"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[16px] font-bold text-[#111] flex-1">Mi perfil</h1>
        <button
          onClick={() => showToast('⚙️ Configuración — próximamente')}
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#555]"
          type="button"
          aria-label="Configuración"
        >
          <Settings className="w-4.5 h-4.5" />
        </button>
      </div>

      <div className="px-4">
        <div className="bg-gradient-to-r from-[#f8f9fa] to-[#f3f7ff] rounded-[20px] p-4 flex items-center gap-3.5 border border-[#ecf0f5] shadow-sm animate-fade-in">
          <div className="relative shrink-0">
            <Avatar user={user} size="w-[68px] h-[68px] text-2xl" showStatus={false} />
            <button
              onClick={() => showToast('Cambiar foto')}
              className="absolute -bottom-0.5 -right-0.5 h-[22px] w-[22px] rounded-full bg-[#7a9a52] border-2 border-white text-white flex items-center justify-center"
              type="button"
              aria-label="Editar avatar"
            >
              <Camera className="w-3 h-3" />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[16px] font-bold text-[#111] truncate">
              {`${user?.nombre || ''} ${user?.apellido || ''}`.trim() || 'Usuario'}
            </p>
            <p className="text-[12px] text-[#888] truncate">{user?.telefono || user?.email || '-'}</p>
            <p className="text-[11px] text-[#8f9aa6] mt-0.5">
              {userTypeLabel(user)}
            </p>
            <button
              onClick={() => navigate('/perfil/editar')}
            className="mt-2 inline-block text-[12px] font-semibold bg-[#5b87d8] text-white px-3 py-1.5 rounded-[20px] shadow-sm transition-transform duration-200 active:scale-[0.98]"
              type="button"
            >
              Editar perfil
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 space-y-2.5">
        <MenuGroup items={menuItemsMain} />
        <MenuGroup items={menuItemsSecondary} />
        <MenuGroup items={menuItemsDanger} />

        <p className="text-center text-[10px] text-[#c5cbd2] font-mono py-2">
          Artemis API v1.4 · {userTypeLabel(user)}
        </p>
      </div>

      {toastMessage ? (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 rounded-full bg-[#111] text-white text-[12px] px-4 py-2 shadow-lg z-[120]">
          {toastMessage}
        </div>
      ) : null}
    </div>
  )
}

function MenuGroup({ items }) {
  return (
    <div className="bg-[#f8f9fa] rounded-2xl overflow-hidden">
      {items.map((item, idx) => {
        const Icon = item.icon
        const isClickable = typeof item.onClick === 'function'
        const Component = isClickable ? 'button' : 'div'
        const isNotifBadge = item.label === 'Notificaciones' && item.value
        return (
          <Component
            key={item.label}
            onClick={item.onClick}
            {...(isClickable ? { type: 'button' } : {})}
            className={`w-full px-3.5 py-3 flex items-center gap-3 text-left transition-colors duration-200 hover:bg-white/70 ${idx !== items.length - 1 ? 'border-b border-[#f0f0f0]' : ''}`}
          >
            <span className={`h-8 w-8 rounded-[9px] flex items-center justify-center ${item.danger ? 'bg-[#fde8e8] text-[#e05555]' : 'bg-[#e8f0fd] text-[#5b87d8]'}`}>
              <Icon className="w-4 h-4" />
            </span>
            <span className={`flex-1 text-[13px] ${item.danger ? 'text-[#e05555]' : 'text-[#111]'} font-medium`}>{item.label}</span>
            {item.value ? (
              <span className={`text-[11px] ${isNotifBadge ? 'bg-[#e05555] text-white px-2 py-0.5 rounded-full font-bold' : 'text-[#aaa]'}`}>
                {item.value}
              </span>
            ) : null}
            {isClickable ? <ChevronRight className="w-4 h-4 text-[#ccc]" /> : null}
          </Component>
        )
      })}
    </div>
  )
}
